import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable, of, switchMap } from 'rxjs';

import {
  DataLossWarningDialogComponent,
  DataLossWarningDialogData
} from '@app/shared/components/invoice/dialogs/data-loss-warning/data-loss-warning.dialog.component';
import {
  DataLossWarningDialogAction
} from '@app/shared/components/invoice/dialogs/data-loss-warning/data-loss-warning-dialog.constants';

export type CanDeactivateRouteFn = boolean | Observable<boolean>;

export interface CanDeactivateRouteComponent {
  canLeaveRoute: () => CanDeactivateRouteFn;
}

/**
 * Factory function to create a CanDeactivate guard with a specific message key.
 */
export function ExitDialogWarningWithoutUnsaveChanges(
    messageKey: string = 'MODALS.DEFAULT.LEAVE_WARNING'
): CanDeactivateFn<CanDeactivateRouteComponent> {
  return (component) => {
    const matDialog = inject(MatDialog);

    const result = component.canLeaveRoute?.() ?? true;

    // ✅ Case 1: direct boolean
    if (typeof result === 'boolean') {
      return result ? true : openDialog(matDialog, messageKey);
    }

    // ✅ Case 2: observable<boolean>
    return result.pipe(
      switchMap((canLeave) => {
        if (canLeave) {return of(true);}
        return openDialog(matDialog, messageKey);
      })
    );
  };
}

/**
 * Opens the confirmation dialog and resolves to true/false.
 */
function openDialog(matDialog: MatDialog, messageKey: string): Observable<boolean> {
  return matDialog
    .open<DataLossWarningDialogComponent, DataLossWarningDialogData, DataLossWarningDialogAction>(
      DataLossWarningDialogComponent,
      {
        panelClass: 'data-loss-warning-dialog',
        data: { messageKey },
      },
    )
    .afterClosed()
    .pipe(map((action) => action === 'EXIT_WITHOUT_SAVING'));
}
